import type {Page, Result} from 'framework';
import type {
  CreateScheduleOneOffInput,
  ListScheduleTemplatesInput,
  MoveScheduleOccurrenceInput,
  StartedScheduleWorkout,
  SwapScheduleOccurrenceInput,
} from '../application/ports/schedule.repository';
import type {CreateScheduleOneOff} from '../application/use-cases/create-schedule-one-off';
import type {GetActiveScheduleWorkout} from '../application/use-cases/get-active-schedule-workout';
import type {GetScheduleCalendar} from '../application/use-cases/get-schedule-calendar';
import type {GetScheduleWeek} from '../application/use-cases/get-schedule-week';
import type {ListScheduleTemplates} from '../application/use-cases/list-schedule-templates';
import type {MoveScheduleOccurrence} from '../application/use-cases/move-schedule-occurrence';
import type {RemoveScheduleOneOff} from '../application/use-cases/remove-schedule-one-off';
import type {RestoreScheduleOccurrence} from '../application/use-cases/restore-schedule-occurrence';
import type {SaveScheduleWeek} from '../application/use-cases/save-schedule-week';
import type {SkipScheduleOccurrence} from '../application/use-cases/skip-schedule-occurrence';
import type {StartScheduleOccurrence} from '../application/use-cases/start-schedule-occurrence';
import type {SwapScheduleOccurrence} from '../application/use-cases/swap-schedule-occurrence';
import type {ActiveScheduleWorkout} from '../domain/active-schedule-workout';
import type {ScheduleError} from '../domain/errors';
import type {ScheduleCalendar, ScheduleCalendarRange} from '../domain/schedule-calendar';
import type {ScheduleOccurrence} from '../domain/schedule-occurrence';
import type {ScheduleOneOffId} from '../domain/schedule-one-off-id.value-object';
import type {ScheduleTemplateOption} from '../domain/schedule-template-option';
import type {SaveScheduleWeekEntry, ScheduleWeekSlot} from '../domain/schedule-week-slot';

/**
 * Frontend-facing schedule API. `today` always comes back from the server with the
 * calendar, so the client never invents the current day from its own clock.
 */
export class ScheduleController {
  constructor(
    private readonly getScheduleCalendar: GetScheduleCalendar,
    private readonly getScheduleWeek: GetScheduleWeek,
    private readonly saveScheduleWeek: SaveScheduleWeek,
    private readonly createScheduleOneOff: CreateScheduleOneOff,
    private readonly removeScheduleOneOff: RemoveScheduleOneOff,
    private readonly skipScheduleOccurrence: SkipScheduleOccurrence,
    private readonly restoreScheduleOccurrence: RestoreScheduleOccurrence,
    private readonly moveScheduleOccurrence: MoveScheduleOccurrence,
    private readonly swapScheduleOccurrence: SwapScheduleOccurrence,
    private readonly startScheduleOccurrence: StartScheduleOccurrence,
    private readonly listScheduleTemplates: ListScheduleTemplates,
    private readonly getActiveScheduleWorkout: GetActiveScheduleWorkout,
  ) {}

  getCalendar(
    range: ScheduleCalendarRange | null,
  ): Promise<Result<ScheduleCalendar, ScheduleError>> {
    return this.getScheduleCalendar.execute(range);
  }

  getWeek(): Promise<Result<readonly ScheduleWeekSlot[], ScheduleError>> {
    return this.getScheduleWeek.execute();
  }

  saveWeek(entries: readonly SaveScheduleWeekEntry[]): Promise<Result<void, ScheduleError>> {
    return this.saveScheduleWeek.execute({entries});
  }

  addOneOff(input: CreateScheduleOneOffInput): Promise<Result<void, ScheduleError>> {
    return this.createScheduleOneOff.execute(input);
  }

  removeOneOff(oneOffId: ScheduleOneOffId): Promise<Result<void, ScheduleError>> {
    return this.removeScheduleOneOff.execute(oneOffId);
  }

  skipOccurrence(occurrence: ScheduleOccurrence): Promise<Result<void, ScheduleError>> {
    return this.skipScheduleOccurrence.execute(occurrence);
  }

  restoreOccurrence(occurrence: ScheduleOccurrence): Promise<Result<void, ScheduleError>> {
    return this.restoreScheduleOccurrence.execute(occurrence);
  }

  moveOccurrence(input: MoveScheduleOccurrenceInput): Promise<Result<void, ScheduleError>> {
    return this.moveScheduleOccurrence.execute(input);
  }

  swapOccurrence(input: SwapScheduleOccurrenceInput): Promise<Result<void, ScheduleError>> {
    return this.swapScheduleOccurrence.execute(input);
  }

  startOccurrence(
    occurrence: ScheduleOccurrence,
  ): Promise<Result<StartedScheduleWorkout, ScheduleError>> {
    return this.startScheduleOccurrence.execute(occurrence);
  }

  listTemplates(
    input: ListScheduleTemplatesInput,
  ): Promise<Result<Page<ScheduleTemplateOption>, ScheduleError>> {
    return this.listScheduleTemplates.execute(input);
  }

  getActive(): Promise<Result<ActiveScheduleWorkout | null, ScheduleError>> {
    return this.getActiveScheduleWorkout.execute();
  }
}
